import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, Warning } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { StatusBadge } from "../StatusBadge";
import { MARKETPLACES, PLATFORM_BY_KEY } from "../../platforms";
import { fmtDateTime, fmtRelative } from "../../campaignUtils";

// Read-only side panel for a queue entry. `row` = { item, promo, post, status, error, events }.
export function QueueDetailPanel({ row, onClose }) {
  const { t } = useTranslation();
  if (!row) return <AnimatePresence />;
  const { item, promo, post, status, error, events = [] } = row;
  const mkt = MARKETPLACES.find((m) => m.key === promo.source_platform);
  const plat = PLATFORM_BY_KEY[item.platform];

  const Field = ({ label, value }) => value == null || value === "" ? null : (
    <div><div className="promo-overline">{label}</div><div className="mt-1 text-sm font-medium">{value}</div></div>
  );

  return (
    <AnimatePresence>
      {row && (
        <div className="fixed inset-0 z-[60]" data-testid="queue-detail-panel">
          <motion.div className="absolute inset-0 bg-background/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-border bg-card"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.25 }}>
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div className="flex items-center gap-2">{plat && <plat.Icon size={16} />}<span className="promo-overline">{plat?.label || item.platform}</span></div>
              <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent" data-testid="queue-panel-close"><X size={18} /></button>
            </div>

            <div className="flex-1 space-y-5 overflow-y-auto p-5">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-medium tracking-tight">{item.product_external_id}</h2>
                <StatusBadge status={status} label={t(`promo.queue.status.${status}`, status)} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label={t("promo.queue.f.campaign")} value={promo.name} />
                <Field label={t("promo.queue.f.marketplace")} value={mkt?.label || promo.source_platform} />
                <Field label={t("promo.queue.f.platform")} value={plat?.label || item.platform} />
                <Field label={t("promo.queue.f.scheduled")} value={fmtDateTime(item.planned_publish_at || post?.scheduled_at)} />
                <Field label={t("promo.queue.f.publish")} value={post?.published_at ? fmtDateTime(post.published_at) : null} />
                <Field label={t("promo.queue.f.created")} value={fmtDateTime(item.created_at)} />
              </div>

              {error && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-4" data-testid="queue-panel-error">
                  <div className="mb-1.5 flex items-center gap-2 text-destructive"><Warning size={15} weight="fill" /><span className="promo-overline">{t("promo.queue.f.lastError")}</span></div>
                  <p className="whitespace-pre-line text-xs text-foreground/90">{error}</p>
                </div>
              )}

              <div>
                <div className="promo-overline mb-2">{t("promo.detail.recentEvents")}</div>
                {events.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("promo.detail.noEvents")}</p>
                ) : (
                  <div className="relative space-y-3 border-l border-border pl-5">
                    {events.slice(0, 8).map((e) => (
                      <div key={e.id} className="relative">
                        <span className={cn("absolute -left-[23px] top-1 h-1.5 w-1.5 rounded-full", e.type.includes("failed") ? "bg-destructive" : "bg-brand")} />
                        <div className="flex items-center justify-between gap-3">
                          <span className="truncate font-mono text-xs">{e.type}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">{fmtRelative(e.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
